import { Fragment } from 'react';
import {
  Badge,
  Button,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type BadgeProps,
  type ButtonProps,
} from '@monksflow/monks-ui';
import { ArrowLeftIcon, XIcon } from 'lucide-react';
import { VersionSelector, type VersionChipConfig } from '../../VersionSelector';

import type { SidePanelHeaderProps, ActionButton } from './SidePanelHeader.types';
import { useTranslation } from 'react-i18next';

/**
 * Chip configs arrive naming a hue; Tessra `Badge` carries intent, so the palette
 * names collapse onto the status variants plus a neutral default.
 */
const CHIP_VARIANT: Record<NonNullable<VersionChipConfig['color']>, BadgeProps['variant']> = {
  red: 'destructive',
  orange: 'warning',
  yellow: 'warning',
  green: 'positive',
  blue: 'default',
  lightBlue: 'default',
  pink: 'default',
  grey: 'secondary',
};

/**
 * Action configs still name their emphasis in MUI's vocabulary because several widget
 * header configs pass those values; the translation stops at this boundary.
 */
const BUTTON_VARIANT: Record<
  NonNullable<Extract<ActionButton, { type: 'button' }>['variant']>,
  ButtonProps['variant']
> = {
  text: 'ghost',
  outlined: 'outline',
  contained: 'default',
};

/** Wraps `children` in a tooltip only when there is tooltip text to show. */
function WithTooltip({ tooltip, children }: { tooltip?: string; children: React.ReactNode }) {
  if (!tooltip) return <>{children}</>;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Module-scope: used inside two separate `.map()` calls (`actionGroups`
// and the flat `actions` fallback), closes over nothing — every value it
// needs already arrives as a parameter.
function ActionButtonItem({ action }: { action: ActionButton }) {
  if (action.type === 'custom') {
    return (
      <WithTooltip tooltip={action.tooltip}>
        <div>{action.component}</div>
      </WithTooltip>
    );
  }

  if (action.type === 'button') {
    const iconAtEnd = action.iconPosition === 'end';

    return (
      <WithTooltip tooltip={action.tooltip}>
        <Button
          onClick={action.onClick}
          disabled={action.disabled}
          aria-label={action.ariaLabel || action.tooltip}
          variant={BUTTON_VARIANT[action.variant ?? 'text']}
        >
          {!iconAtEnd && action.icon}
          {action.label}
          {iconAtEnd && action.icon}
        </Button>
      </WithTooltip>
    );
  }

  return (
    <WithTooltip tooltip={action.tooltip}>
      <Button
        variant="ghost"
        size="icon"
        onClick={action.onClick}
        disabled={action.disabled}
        aria-label={action.ariaLabel || action.tooltip || ''}
      >
        {action.icon}
      </Button>
    </WithTooltip>
  );
}

/**
 * Header for a side panel: an optional back button or version selector plus a chip and
 * title on the left; action buttons and a close button on the right.
 *
 * Design spec: https://www.figma.com/design/hVKNd1TNmfgDDXkIXXKQrg/MF-2.0-UI-LIBRARY?node-id=4515-2195
 *
 * @example
 * <SidePanelHeader title="My Panel" onClose={handleClose} />
 *
 * @example
 * <SidePanelHeader
 *   title="Strategy Details"
 *   chip={{ label: 'Strategy', color: 'grey' }}
 *   onClose={handleClose}
 * />
 *
 * @example
 * <SidePanelHeader
 *   title="Email Flow"
 *   actions={[
 *     { type: 'icon', icon: <DownloadIcon />, onClick: handleDownload, tooltip: 'Download' },
 *     { type: 'button', label: 'Save', icon: <SaveIcon />, onClick: handleSave, variant: 'contained', iconPosition: 'end' },
 *   ]}
 *   onClose={handleClose}
 * />
 *
 * @example
 * // Groups are separated by a vertical rule: Toggle | Copy
 * actionGroups={[
 *   [{ type: 'custom', component: <ToggleSwitch /> }],
 *   [{ type: 'icon', icon: <CopyIcon />, onClick: handleCopy }],
 * ]}
 */
export const SidePanelHeader = ({
  title,
  chip,
  backButton,
  actions = [],
  actionGroups,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  headerAction,
  onClose,
  showDivider = false,
  versionConfig,
}: SidePanelHeaderProps) => {
  const { t } = useTranslation('common');

  return (
    <>
      <div className="flex h-16 shrink-0 items-center border-b border-border-lighter px-3 pr-6">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
          {backButton && (
            <Button variant="outline" onClick={backButton.onClick}>
              <ArrowLeftIcon />
              {backButton.label}
            </Button>
          )}

          {versionConfig ? (
            <VersionSelector
              currentVersion={versionConfig.currentVersion}
              versions={versionConfig.versions}
              onVersionSelect={versionConfig.onVersionSelect}
              chip={chip}
            />
          ) : (
            <>
              {chip && (
                <div className="flex shrink-0 flex-row items-center gap-2">
                  <Badge
                    variant={chip.color ? CHIP_VARIANT[chip.color] : 'secondary'}
                    data-testid="chip-label"
                  >
                    {chip.label}
                  </Badge>
                </div>
              )}

              {title && (
                <span className="truncate text-body-lg-strong font-medium text-foreground">
                  {title}
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-row items-center gap-1">
          {/* Custom header action (for backward compatibility) */}
          {headerAction}

          {actionGroups
            ? actionGroups.map((group, groupIndex) => (
                <Fragment key={groupIndex}>
                  {group.map((action, actionIndex) => (
                    <ActionButtonItem key={`${groupIndex}-${actionIndex}`} action={action} />
                  ))}
                  {groupIndex < actionGroups.length - 1 && (
                    <Separator orientation="vertical" decorative={false} className="mx-1" />
                  )}
                </Fragment>
              ))
            : actions.map((action, index) => <ActionButtonItem key={index} action={action} />)}

          {onClose && (
            <WithTooltip tooltip={t('components.sidePanelHeader.close')}>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label={t('components.sidePanelHeader.close2')}
              >
                <XIcon />
              </Button>
            </WithTooltip>
          )}
        </div>
      </div>

      {showDivider && <Separator className="mx-10" />}
    </>
  );
};
